// src/components/ReportModal.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const PAID_SEARCH_PLATFORMS = ['Google Ads', 'Microsoft', 'Meta', 'Yelp', 'Nextdoor'];

const CHANNEL_LABELS = {};

function getPlat(client, name) {
  return (client.platforms || []).find(p => p.name === name) || null;
}

function getAllPlats(client, name) {
  return (client.platforms || []).filter(p => p.name === name);
}

function cidList(client, name) {
  return getAllPlats(client, name).map(p => p.cid).filter(Boolean).join(', ');
}

function budgetSum(client, name) {
  const total = getAllPlats(client, name).reduce((sum, p) => sum + (parseFloat(p.budget) || 0), 0);
  return total > 0 ? `$${total.toLocaleString()}` : '';
}

function line(label, value = '') {
  return `${label}${value}`;
}

// Builds the plain-text launch email body from client data + section toggles
function buildReport(client, sections) {
  const parts = [];
  const platforms = client.platforms || [];
  const lsaPlats = getAllPlats(client, 'LSA');

  const presentPaidSearch = PAID_SEARCH_PLATFORMS.filter(name => platforms.some(p => p.name === name));

  // Launch date: latest launched platform date, fallback to actualLaunch
  const launchDates = platforms.filter(p => p.launched && p.launchDate).map(p => p.launchDate).sort();
  const launchDate = launchDates.length ? launchDates[launchDates.length - 1] : (client.actualLaunch || '');

  // ---- Header / greeting ----
  if (sections.header) {
    parts.push(`Hi ${client.strategist || '{PMS}'},`);
    parts.push('');
    parts.push(`${client.name || '{Account Name}'}'s paid media campaigns have officially launched today and will be assigned to your team for ongoing management. The account has been successfully ingested into AdPulse, we have created a parent budget and we have applied 'auto-protect'. You will need to apply child budgets and settings accordingly.`);
    parts.push('');
    parts.push('Special Notes');
    parts.push('');
    const specialLines = (client.specialNotesLaunch || '').split('\n').map(l => l.trim()).filter(Boolean);
    if (specialLines.length) {
      specialLines.forEach(l => parts.push(`* ${l}`));
    } else {
      parts.push('* ');
    }
    parts.push('');
  }

  // ---- Account overview ----
  if (sections.overview) {
    parts.push(`PM: ${client.pm || ''} & CSM: ${client.csm || ''}`);
    parts.push('');
    parts.push('Channels:');
    parts.push('');
    platforms.forEach(p => parts.push(`* ${CHANNEL_LABELS[p.name] || p.name}`));
    parts.push('');
    parts.push(line('LSA CID: ', cidList(client, 'LSA')));
    parts.push(line('Google Ads CID: ', cidList(client, 'Google Ads')));
    parts.push(line('Microsoft ID: ', cidList(client, 'Microsoft')));
    parts.push(line('Meta ID: ', cidList(client, 'Meta')));
    parts.push('');
    parts.push(line('Launch Date: ', launchDate || ''));
    parts.push('');
    parts.push(line('Client Existing Website: ', client.websiteUrl || ''));
    parts.push('');
    parts.push('Channel Budgets:');
    parts.push('');
    parts.push(line('* LSA: ', budgetSum(client, 'LSA')));
    parts.push(line('* Google Ads: ', budgetSum(client, 'Google Ads')));
    parts.push(line('* Microsoft: ', budgetSum(client, 'Microsoft')));
    parts.push(line('* Meta: ', budgetSum(client, 'Meta')));
    parts.push('');
  }

  // ---- LSA overview — one block per LSA profile/CID present ----
  if (sections.lsa && lsaPlats.length > 0) {
    lsaPlats.forEach((lsaPlat, i) => {
      const weekly = lsaPlat?.budget ? Math.round((parseFloat(lsaPlat.budget) || 0) / 4.33) : '';
      const heading = lsaPlats.length > 1
        ? `Here is a quick overview of the LSA account${lsaPlat.cid ? ` (${lsaPlat.cid})` : ` #${i + 1}`}:`
        : 'Here is a quick overview of the LSA account:';
      parts.push(heading);
      parts.push(`CID Information: ${lsaPlat?.cid || ''}`);
      parts.push('Example: We paused the old account {Insert CID Here} and enabled the new account {Insert CID Here}');
      parts.push('Budgets:');
      parts.push(`* Weekly budget is set at ${weekly ? '$' + weekly.toLocaleString() : '$0,000'}`);
      parts.push(`* Total Monthly Budget ${lsaPlat?.budget ? '$' + Number(lsaPlat.budget).toLocaleString() : '$0,000'}`);
      parts.push(line('Phone Number: ', client.phoneNumbers || ''));
      parts.push("**A request has been made for Google to update the LSA phone number to the client's RYNO tracked line. This process can take 24-48 hours**");
      parts.push('Job Types: {Insert Job Types Here}');
      parts.push('* Job Type:');
      parts.push('Active:');
      parts.push('Toggled Off:');
      parts.push(line('Business Hours: ', client.businessHours || ''));
      parts.push('Ad Schedule: 24/7');
      parts.push('Insurance:  Licensing:');
      parts.push('Settings:');
      parts.push('Message Leads:');
      parts.push('Toggled On | Off');
      parts.push('Messaging Preferences for receiving leads:');
      parts.push('* Email');
      parts.push('* Text Message (xxx) xxx-xxx');
      parts.push('Direct Business Search:');
      parts.push('Toggled On | Off');
      parts.push(line('Service Areas Targeting: ', client.serviceAreaNotes || '{List Location Targeting Here}'));
      parts.push('{Add Image of map here}');
      parts.push('Business Bio:  <- copy & paste from LSA Profile');
      parts.push('');
    });
  }

  // ---- Paid Search overview (one block per platform instance — handles multiple accounts of the same platform) ----
  presentPaidSearch.forEach(name => {
    if (!sections.paidSearch?.[name]) return;
    const plats = getAllPlats(client, name);
    plats.forEach((plat, i) => {
      const heading = plats.length > 1
        ? `Here is a quick overview of the ${name} account${plat.cid ? ` (${plat.cid})` : ` #${i + 1}`}:`
        : `Here is a quick overview of the ${name} account:`;
      parts.push(heading);
      parts.push(line('Monthly Budget: $', plat?.budget ? Number(plat.budget).toLocaleString() : ''));
      parts.push(line(`${name} CID: `, plat?.cid || ''));
      parts.push("Payment Method: Client's Own Credit Card");
      parts.push(line('Ads Location: ', client.adsDestination || ''));
      parts.push(line('Ad Schedule: ', client.adScheduleNotes || ''));
      parts.push(line('Phone Number: ', client.phoneNumbers || ''));
      parts.push('Call Extensions');
      (client.verticals && client.verticals.length ? client.verticals : ['Brand']).forEach(v => {
        parts.push(line(`${v}: `, ''));
      });
      parts.push(line('F/R Phone Number: ', client.phoneNumbers || ''));
      parts.push('On LPs');
      parts.push(line('Contact Form Email: ', client.formContactEmail || ''));
      parts.push(line('Location Targeting: ', client.serviceAreaNotes || ''));
      parts.push('Requested But Not targetable in GA:');
      parts.push('OB Analysis:');
      parts.push('');
    });
  });

  if (sections.header) {
    parts.push('Let me know if you have any questions.');
  }

  return parts.join('\n');
}

function CheckRow({ label, checked, onChange, disabled, hint }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 13,
      padding: '7px 9px', borderRadius: 7, cursor: disabled ? 'not-allowed' : 'pointer',
      background: checked ? '#E6F1FB' : 'transparent', opacity: disabled ? 0.5 : 1,
    }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={e => onChange(e.target.checked)}
        style={{ marginTop: 2, width: 15, height: 15, accentColor: '#185FA5', flexShrink: 0 }}
      />
      <span>
        <div style={{ fontWeight: 600 }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{hint}</div>}
      </span>
    </label>
  );
}

export default function ReportModal({ client, onClose, onSaved }) {
  const platforms = client.platforms || [];
  const hasLSA = platforms.some(p => p.name === 'LSA');
  const presentPaidSearch = useMemo(
    () => PAID_SEARCH_PLATFORMS.filter(name => platforms.some(p => p.name === name)),
    [platforms]
  );

  const savedSections = client.reportSections;
  const defaultSections = () => ({
    header: true,
    overview: true,
    lsa: hasLSA,
    paidSearch: presentPaidSearch.reduce((acc, n) => ({ ...acc, [n]: true }), {}),
  });

  const [stage, setStage] = useState(client.reportContent ? 'edit' : 'select'); // 'select' | 'edit'
  const [sections, setSections] = useState(savedSections || defaultSections());
  const [content, setContent] = useState(client.reportContent || '');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(client.reportUpdatedAt || null);
  const [copyMsg, setCopyMsg] = useState('');
  const taRef = useRef(null);

  useEffect(() => {
    // If nothing generated yet, jump straight into an initial generation on open
    if (!client.reportContent) {
      setContent(buildReport(client, savedSections || defaultSections()));
    }
  }, []); // eslint-disable-line

  function toggleSection(key, val) {
    setSections(s => ({ ...s, [key]: val }));
  }
  function togglePlatform(name, val) {
    setSections(s => ({ ...s, paidSearch: { ...s.paidSearch, [name]: val } }));
  }

  function handleGenerate() {
    setContent(buildReport(client, sections));
    setStage('edit');
  }

  async function persist(nextContent, nextSections) {
    const fields = {
      reportContent: nextContent,
      reportSections: nextSections,
      reportUpdatedAt: Date.now(),
    };
    try {
      await setDoc(doc(db, 'clients', client.id), fields, { merge: true });
      setSavedAt(fields.reportUpdatedAt);
      onSaved && onSaved(fields);
    } catch (e) {
      console.error('Autosave failed:', e);
    }
  }

  // Autosave a couple seconds after typing stops, so nothing is ever lost
  const firstRun = useRef(true);
  useEffect(() => {
    if (stage !== 'edit') return;
    if (firstRun.current) { firstRun.current = false; return; }
    const t = setTimeout(() => { persist(content, sections); }, 1800);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [content]);

  function handleClose() {
    if (stage === 'edit' && content) persist(content, sections);
    onClose();
  }

  async function handleSave() {
    setSaving(true);
    await persist(content, sections);
    setSaving(false);
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(content);
      setCopyMsg('Copied ✓');
    } catch {
      taRef.current?.select();
      document.execCommand('copy');
      setCopyMsg('Copied ✓');
    }
    setTimeout(() => setCopyMsg(''), 1800);
  }

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    zIndex: 260, padding: 20, overflowY: 'auto',
  };
  const modalStyle = {
    background: '#fff', borderRadius: 12, width: '100%', maxWidth: 760,
    border: '1px solid rgba(0,0,0,0.15)', margin: 'auto',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  };

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={{
          padding: '13px 18px', borderBottom: '1px solid rgba(0,0,0,0.1)',
          display: 'flex', alignItems: 'center', gap: 10,
          position: 'sticky', top: 0, background: '#fff',
          borderRadius: '12px 12px 0 0', zIndex: 1,
        }}>
          <div style={{ fontSize: 16, fontWeight: 800, flex: 1, color: '#1a1a18' }}>
            📋 Launch Report — {client.name}
          </div>
          <button onClick={handleClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 18, color: '#999', padding: '2px 6px',
          }}>✕</button>
        </div>

        {stage === 'select' ? (
          <div style={{ padding: 18 }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
              Choose which sections to include. You can regenerate later if you change your mind — anything you type in afterward is saved separately from these choices.
            </div>

            <CheckRow
              label="Greeting, launch summary & Special Notes"
              checked={sections.header}
              onChange={v => toggleSection('header', v)}
            />
            <CheckRow
              label="Account Overview"
              hint="PM/CSM, channels, CIDs, launch date, website, channel budgets"
              checked={sections.overview}
              onChange={v => toggleSection('overview', v)}
            />
            {hasLSA && (
              <CheckRow
                label="LSA Account Overview"
                hint={platforms.filter(p => p.name === 'LSA').length > 1 ? `Includes all ${platforms.filter(p => p.name === 'LSA').length} LSA profiles on this account` : undefined}
                checked={sections.lsa}
                onChange={v => toggleSection('lsa', v)}
              />
            )}
            {presentPaidSearch.length > 0 && (
              <div style={{ marginTop: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '6px 9px' }}>
                  Paid Search Account Overview
                </div>
                {presentPaidSearch.map(name => {
                  const count = platforms.filter(p => p.name === name).length;
                  return (
                    <CheckRow
                      key={name}
                      label={`${name} overview block`}
                      hint={count > 1 ? `Includes all ${count} ${name} accounts on this client` : undefined}
                      checked={!!sections.paidSearch?.[name]}
                      onChange={v => togglePlatform(name, v)}
                    />
                  );
                })}
              </div>
            )}
            {platforms.length === 0 && (
              <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 10 }}>
                This client has no platforms added yet, so there's nothing to pull CIDs/budgets from — add platforms first for a fuller autofill.
              </div>
            )}
          </div>
        ) : (
          <div style={{ padding: 18 }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
              Autofilled from the client card where possible — everything else (marked with placeholders like <code>{'{...}'}</code>) still needs to be filled in by hand. Edit freely below, then Save or Copy.
            </div>
            <textarea
              ref={taRef}
              value={content}
              onChange={e => setContent(e.target.value)}
              style={{
                width: '100%', minHeight: 480, fontSize: 13, lineHeight: 1.5,
                padding: 14, border: '1px solid rgba(0,0,0,0.18)', borderRadius: 8,
                background: '#fafaf8', color: '#1a1a18', fontFamily: 'inherit',
                resize: 'vertical', boxSizing: 'border-box',
              }}
            />
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '12px 18px', borderTop: '1px solid rgba(0,0,0,0.1)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'sticky', bottom: 0, background: '#fff',
          borderRadius: '0 0 12px 12px', gap: 8,
        }}>
          <div style={{ fontSize: 11, color: '#888' }}>
            {stage === 'edit' && savedAt ? `Last saved ${new Date(savedAt).toLocaleString()}` : ''}
            {copyMsg && <span style={{ color: '#0F6E56', fontWeight: 700, marginLeft: 8 }}>{copyMsg}</span>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {stage === 'edit' && (
              <button onClick={() => setStage('select')} style={{
                fontSize: 12, padding: '7px 14px', borderRadius: 7,
                background: '#f5f5f3', color: '#333', border: '1px solid rgba(0,0,0,0.18)',
                cursor: 'pointer', fontFamily: 'inherit',
              }}>
                ← Edit Sections
              </button>
            )}
            <button onClick={handleClose} style={{
              fontSize: 12, padding: '7px 14px', borderRadius: 7,
              background: '#f5f5f3', color: '#333', border: '1px solid rgba(0,0,0,0.18)',
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
              Close
            </button>
            {stage === 'select' ? (
              <button onClick={handleGenerate} style={{
                fontSize: 12, padding: '7px 18px', borderRadius: 7,
                background: '#185FA5', color: '#fff', border: 'none',
                cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700,
              }}>
                Generate Report →
              </button>
            ) : (
              <>
                <button onClick={handleCopy} style={{
                  fontSize: 12, padding: '7px 14px', borderRadius: 7,
                  background: '#EAF3DE', color: '#27500A', border: '1px solid #b8dfa0',
                  cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
                }}>
                  ⧉ Copy
                </button>
                <button onClick={handleSave} disabled={saving} style={{
                  fontSize: 12, padding: '7px 18px', borderRadius: 7,
                  background: saving ? '#aaa' : '#185FA5', color: '#fff', border: 'none',
                  cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontWeight: 700,
                }}>
                  {saving ? 'Saving…' : '💾 Save Report'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
